import React, { useEffect } from 'react';
import { useWindowSize } from 'react-use';
import { Breakpoint, theme } from '@noths/polaris-client-ribbons-base';
import { DrawerHeader, Media } from '@noths/polaris-client-ribbons-design-system';
import { pxToRem } from '@noths/polaris-client-styles';
import { useRouter } from 'next/router';

import * as copy from '../constants/copy';
import { DESKTOP_FILTER_HEIGHT_CSS_VAR } from '../constants/cssVariables';
import { FilterUid } from '../constants/uids';
import type { FilterListProps } from '../containers/FilterListContainer.types';
import { FilterControls } from './FilterControls';
import { FilterListItem } from './FilterListItem';
import { ListingsApiError } from './ListingsApiError';
import * as styles from '../styles/FilterList.styles';
import * as modalStyles from '../styles/FilterModal.styles';

const setFilterHeight = (windowHeight: number) =>
  document.documentElement.style.setProperty(
    DESKTOP_FILTER_HEIGHT_CSS_VAR,
    `calc(${windowHeight}px - ${pxToRem(theme.spacing.xl)})`,
  );

export const FilterList = ({
  error,
  filters,
  isLoading,
  onAllFiltersVisible,
  onCloseFilters,
  onDeliveryFilterVisible,
  onResetFilters,
  onViewResults,
  totalFiltersAppliedCount,
  totalProducts,
  totalSelectedFilterOptionsCount,
}: FilterListProps) => {
  const router = useRouter();
  const { height } = useWindowSize();

  useEffect(() => {
    setFilterHeight(height);
  }, [height]);

  const handleFilterVisible = (filterUid: string) => {
    const filterIndex = filters.findIndex(({ uid }) => uid === filterUid);
    const lastFilter = filterIndex === filters.length - 1;

    if (filterUid === FilterUid.Delivery) {
      onDeliveryFilterVisible();
    }
    if (lastFilter) {
      onAllFiltersVisible();
    }
  };

  return (
    <form
      css={[modalStyles.modalViewport, styles.filterListWrapper]}
      onSubmit={(e) => e.preventDefault()}
    >
      <Media lessThan={Breakpoint.L}>
        <DrawerHeader
          headingLevel="h2"
          title={copy.FILTER_MODAL_HEADING}
          onClose={onCloseFilters}
        />
      </Media>
      {error ? (
        <ListingsApiError />
      ) : (
        // this shows for everything, not just Desktop
        <ul css={[styles.list, modalStyles.modalScrollableContent]}>
          {filters.map((filter) => {
            if (filter.options.length === 0) {
              return null;
            }

            return (
              <FilterListItem
                filter={filter}
                key={filter.uid}
                onFilterListItemVisibility={handleFilterVisible}
              />
            );
          })}
        </ul>
      )}
      <Media lessThan={Breakpoint.L}>
        <FilterControls
          isLoading={isLoading}
          totalProducts={totalProducts}
          totalSelectedFilterOptionsCount={totalSelectedFilterOptionsCount}
          onViewResults={() =>
            onViewResults({
              totalFiltersAppliedCount,
              totalSelectedFilterOptionsCount,
            })
          }
          resetText={copy.FILTER_LIST_RESET}
          error={error}
          onReset={() => onResetFilters(router)}
        />
      </Media>
    </form>
  );
};
